import LinearProgressWithLabel from 'Components/LinearProgressWithLabel'
import {
  useCancelBulkTicketSaleBatchMutation,
  useGetBulkTicketSaleBatchProgressQuery,
  useGetSingleBulkTicketSaleBatchQuery,
  useLazyGetBulkTicketSaleBatchDownloadUrlQuery,
  useRetryBulkTicketSaleBatchMutation
} from 'Services/SalesManagement/Features/TicketSale/ticketSaleApi'
import {
  BatchStatus,
  type BulkTicketSaleBatch,
  type BulkTicketSaleBatchProgress
} from 'Services/SalesManagement/Features/TicketSale/types'
import { isBatchCancellable } from 'Services/SalesManagement/Features/TicketSale/utils'
import { REPORT_DOWNLOAD_URL_ALLOWED_HOST_NAMES } from 'Util/constants'
import { datetimeFormatterWithoutSeconds } from 'Util/formatters'
import { useConfirm } from 'Util/hooks/useConfirm'
import SnackbarUtils from 'Util/Snackbar'
import CloseIcon from '@mui/icons-material/Close'
import DownloadIcon from '@mui/icons-material/Download'
import {
  Button,
  CircularProgress,
  IconButton,
  TableCell,
  TableRow,
  Typography
} from '@mui/material'
import { useMemo } from 'react'
import { t } from 'ttag'

interface BatchProgressStatusProps {
  batchProgress: BulkTicketSaleBatchProgress | undefined
}

function BatchProgressStatus({ batchProgress }: Readonly<BatchProgressStatusProps>) {
  switch (batchProgress?.status) {
    case BatchStatus.QUEUED:
    case BatchStatus.PROCESSING:
      return t`Processing`
    case BatchStatus.PROCESSED:
    case BatchStatus.PARTIALLY_PROCESSED:
      return `${batchProgress.processed_count}/${batchProgress.requested_quantity}`
    case BatchStatus.CANCELLING:
      return t`Cancelling`
    case BatchStatus.CANCELLED:
      return t`Cancelled`
    case BatchStatus.PARTIALLY_CANCELLED:
      return t`Partially cancelled`
    case BatchStatus.FAILED:
      return (
        <>
          <Typography>{`${batchProgress.processed_count}/${batchProgress.requested_quantity}`}</Typography>
          <Typography
            fontSize={14}
            color="error"
          >{t`There was an error generating the tickets`}</Typography>
        </>
      )
    default:
      return null
  }
}

function useRetryBatchWithSnackbar(errorMessage: string) {
  const [retryBulkTicketSaleBatch, { isLoading: isRetryLoading }] =
    useRetryBulkTicketSaleBatchMutation()

  const retryBatch = (bulkSaleId: string, batchId: string) => {
    retryBulkTicketSaleBatch({ bulkSaleId, batchId })
      .unwrap()
      .then(() => SnackbarUtils.success(t`Retry successfully started.`))
      .catch(() => SnackbarUtils.error(errorMessage))
  }

  return { retryBatch, isRetryLoading }
}

interface BatchDownloadCellProps {
  bulkSaleId: string
  batchId: string
  batchProgress: BulkTicketSaleBatchProgress | undefined
}

function BatchDownloadCell({
  bulkSaleId,
  batchId,
  batchProgress
}: Readonly<BatchDownloadCellProps>) {
  const progressPercentage = useMemo(() => {
    if (!batchProgress || batchProgress.requested_quantity === 0) return 0
    return (
      ((batchProgress.travel_account_count + batchProgress.processed_count) /
        (2 * batchProgress.requested_quantity)) *
      100
    )
  }, [batchProgress])

  const isCompleted = batchProgress?.status === BatchStatus.PROCESSED

  const { data: batch, isFetching: isBatchFetching } = useGetSingleBulkTicketSaleBatchQuery(
    { bulkSaleId, batchId },
    { skip: !isCompleted }
  )
  const [triggerGetDownloadUrl, { isFetching: isDownloadUrlFetching }] =
    useLazyGetBulkTicketSaleBatchDownloadUrlQuery()
  const { retryBatch, isRetryLoading } = useRetryBatchWithSnackbar(
    t`There was an error retrying ticket creation.`
  )

  const handleDownload = () => {
    if (!batch?.has_csv_file) return

    // Open synchronously on the click so browsers don't treat it as an unrequested popup.
    const downloadWindow = window.open('', '_blank')

    triggerGetDownloadUrl({ bulkSaleId, batchId })
      .unwrap()
      .then(response => {
        let url: URL
        try {
          url = new URL(response.download_url)
        } catch {
          downloadWindow?.close()
          SnackbarUtils.error(t`The download URL is invalid.`)
          return
        }

        if (!REPORT_DOWNLOAD_URL_ALLOWED_HOST_NAMES.includes(url.hostname)) {
          downloadWindow?.close()
          SnackbarUtils.error(t`The download URL is invalid.`)
          return
        }

        if (downloadWindow) {
          downloadWindow.opener = null
          downloadWindow.location.href = response.download_url
        }
      })
      .catch(() => {
        downloadWindow?.close()
        SnackbarUtils.error(t`There was an error fetching the download link.`)
      })
  }

  const handleRetry = () => {
    retryBatch(bulkSaleId, batchId)
  }

  switch (batchProgress?.status) {
    case BatchStatus.QUEUED:
    case BatchStatus.PROCESSING:
      return <LinearProgressWithLabel value={progressPercentage} color="primary" />
    case BatchStatus.FAILED:
      return (
        <Button
          variant="outlined"
          size="medium"
          disableElevation
          onClick={handleRetry}
          loading={isRetryLoading}
        >
          {t`Try again`}
        </Button>
      )
    case BatchStatus.PROCESSED:
      if (isBatchFetching) {
        return <LinearProgressWithLabel value={progressPercentage} color="primary" />
      }
      if (batch?.has_csv_file) {
        return (
          <Button
            variant="outlined"
            size="medium"
            onClick={handleDownload}
            disableElevation
            loading={isDownloadUrlFetching}
            startIcon={<DownloadIcon />}
          >
            {t`Download tickets`}
          </Button>
        )
      }
      return <Typography color="error">{t`Unknown error`}</Typography>
    case BatchStatus.PARTIALLY_PROCESSED:
      return <Typography>{t`Partially processed`}</Typography>
    case BatchStatus.CANCELLING:
      return <Typography>{t`Cancelling`}</Typography>
    case BatchStatus.CANCELLED:
      return <Typography>{t`Cancelled`}</Typography>
    case BatchStatus.PARTIALLY_CANCELLED:
      return <Typography>{t`Partially cancelled`}</Typography>
    default:
      return null
  }
}

interface BatchCancelCellProps {
  bulkSaleId: string
  batch: BulkTicketSaleBatch
  batchProgress: BulkTicketSaleBatchProgress | undefined
}

function BatchCancelCell({ bulkSaleId, batch, batchProgress }: Readonly<BatchCancelCellProps>) {
  const { confirm } = useConfirm()
  const [cancelBulkTicketSaleBatch, { isLoading: isCancelLoading }] =
    useCancelBulkTicketSaleBatchMutation()
  const { retryBatch, isRetryLoading } = useRetryBatchWithSnackbar(
    t`There was an error retrying ticket cancellation.`
  )

  const isCancellable = isBatchCancellable(batchProgress?.status)
  const isCancelling = isCancelLoading || batchProgress?.status === BatchStatus.CANCELLING

  const handleCancel = () => {
    const total = batch.requested_quantity
    confirm({
      title: t`Cancel batch`,
      message: t`If you cancel the batch, all tickets in this batch will be invalidated.\n\nTickets in batch: ${total} pcs`,
      handleConfirm: () => {
        cancelBulkTicketSaleBatch({ bulkSaleId, batchId: batch.id })
          .unwrap()
          .then(() => SnackbarUtils.success(t`Ticket cancellation successfully started.`))
          .catch(() => SnackbarUtils.error(t`Failed to cancel the tickets.`))
      }
    })
  }

  const handleRetryCancel = () => {
    retryBatch(bulkSaleId, batch.id)
  }

  if (batchProgress?.status === BatchStatus.PARTIALLY_CANCELLED) {
    return (
      <Button
        variant="outlined"
        size="medium"
        disableElevation
        onClick={handleRetryCancel}
        loading={isRetryLoading}
      >
        {t`Try again`}
      </Button>
    )
  }

  return (
    <>
      {isCancellable && (
        <IconButton
          size="small"
          color="error"
          sx={{ ml: 1 }}
          onClick={handleCancel}
          disabled={isCancelling}
          title={t`Cancel batch`}
          aria-label={t`Cancel batch`}
          aria-busy={isCancelling}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
      {isCancelling && <CircularProgress size={20} sx={{ ml: 1 }} />}
      {!isCancellable && !isCancelling && <Typography ml={2.5}>-</Typography>}
    </>
  )
}

interface BulkTicketSaleBatchTableRowProps {
  bulkSaleId: string
  batch: BulkTicketSaleBatch
}

export default function BulkTicketSaleBatchTableRow({
  bulkSaleId,
  batch
}: Readonly<BulkTicketSaleBatchTableRowProps>) {
  const { data: batchProgress } = useGetBulkTicketSaleBatchProgressQuery({
    bulkSaleId,
    batchId: batch.id
  })

  return (
    <TableRow hover tabIndex={-1}>
      <TableCell>{datetimeFormatterWithoutSeconds(batch.created)}</TableCell>
      <TableCell>
        <BatchProgressStatus batchProgress={batchProgress} />
      </TableCell>
      <TableCell sx={{ pr: 0 }}>
        <BatchDownloadCell
          bulkSaleId={bulkSaleId}
          batchId={batch.id}
          batchProgress={batchProgress}
        />
      </TableCell>
      <TableCell>
        <BatchCancelCell bulkSaleId={bulkSaleId} batch={batch} batchProgress={batchProgress} />
      </TableCell>
    </TableRow>
  )
}
